import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { Card } from '@components/card';
import { DesignAvatar } from '@components/design-avatar';
import { IconAIMagicGradient } from '@components/icons/ai-magic-gradient';
import { IconList } from '@components/icons/list';
import { IconPortalGradient } from '@components/icons/portal-gradient';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { ResourcePickerData, ResourcePickerItem } from 'api/ai-builder/requests';

const MAX_VISIBLE_ITEMS = 4;
const AI_AGENT_ICON_SIZE = 32;

type AiBuilderResourcePickerProps = {
  resourcePicker: ResourcePickerData;
  onPick: (item: ResourcePickerItem) => Promise<void>;
};

export function AiBuilderResourcePicker({ resourcePicker, onPick }: AiBuilderResourcePickerProps) {
  const [query, setQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filteredItems = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return resourcePicker.items;
    return resourcePicker.items.filter((item) => {
      return (
        item.name.toLowerCase().includes(trimmed) ||
        item.subtitle?.toLowerCase().includes(trimmed) ||
        item.description?.toLowerCase().includes(trimmed)
      );
    });
  }, [query, resourcePicker.items]);

  const visibleItems = useMemo(() => {
    return filteredItems.slice(0, MAX_VISIBLE_ITEMS);
  }, [filteredItems]);

  const handlePick = async (item: ResourcePickerItem) => {
    if (isSubmitting) return;
    setSelectedId(item.id);
    setIsSubmitting(true);
    await onPick(item);
  };

  return (
    <PickerCard>
      <Stack space={1}>
        <Stack orientation="horizontal" space={2} align="center">
          <HeaderIcon $resource={resourcePicker.resource}>
            {resourcePicker.resource === 'portal' ? (
              <IconPortalGradient size={20} />
            ) : resourcePicker.resource === 'company_list' ? (
              <IconList color="elementBlue100" size={18} />
            ) : (
              <IconAIMagicGradient size={18} />
            )}
          </HeaderIcon>
          <Text font="semibold14" text={resourcePicker.title} color="textPrimary" />
        </Stack>
        {resourcePicker.description && (
          <Text font="regular12" text={resourcePicker.description} color="textSecondary" />
        )}
      </Stack>

      <SearchBoxWrapper>
        <SearchBox
          placeholder={`Search ${resourcePicker.title.toLowerCase()}`}
          onChange={setQuery}
        />
      </SearchBoxWrapper>

      {visibleItems.length === 0 ? (
        <EmptyState>
          <Text
            font="regular12"
            text={resourcePicker.emptyMessage ?? 'No matching results'}
            color="textHelper"
          />
        </EmptyState>
      ) : (
        <Stack space={3}>
          {visibleItems.map((item) => {
            const isSelected = selectedId === item.id;
            return (
              <Card
                key={item.id}
                interactive
                isActive={isSelected}
                space={3}
                onClick={() => handlePick(item)}
                style={{ cursor: 'pointer' }}
                role="button"
                aria-pressed={isSelected}
              >
                <Stack orientation="horizontal" align="center" space={2}>
                  <CardIcon $variant={resourcePicker.resource}>
                    {item.iconUrl ? (
                      resourcePicker.resource === 'ai_agent' ? (
                        <DesignAvatar src={item.iconUrl} size={AI_AGENT_ICON_SIZE} />
                      ) : (
                        <CardImage src={item.iconUrl} alt={item.name} />
                      )
                    ) : resourcePicker.resource === 'ai_agent' ? (
                      <Avatar name={item.name} size="lg" color="purple" />
                    ) : resourcePicker.resource === 'portal' ? (
                      <IconPortalGradient size={22} />
                    ) : (
                      <IconList color="elementBlue100" size={20} />
                    )}
                  </CardIcon>
                  <Stack space={1}>
                    <Text font="semibold12" text={item.name} color="textPrimary" />
                    {item.subtitle && resourcePicker.resource !== 'ai_agent' && (
                      <Text font="regular12" text={item.subtitle} color="textSecondary" />
                    )}
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}
    </PickerCard>
  );
}

const PickerCard = styled(Stack).attrs({ space: 3 })({
  width: '100%',
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(10),
  padding: pxToRem(16),
  background: theme.color.backgroundPrimary,
});

const HeaderIcon = styled.div<{ $resource: ResourcePickerData['resource'] }>(({ $resource }) => ({
  width: pxToRem(28),
  height: pxToRem(28),
  borderRadius: pxToRem(8),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    $resource === 'portal'
      ? theme.color.purpleTrans10
      : $resource === 'company_list'
      ? theme.color.blueTrans10
      : theme.color.purpleTrans10,
}));

const SearchBoxWrapper = styled.div({
  '.input-container': {
    borderRadius: pxToRem(8),
  },
});

const EmptyState = styled.div({
  padding: pxToRem(16),
  borderRadius: pxToRem(8),
  background: theme.color.backgroundLightDark,
  textAlign: 'center',
});

const CardIcon = styled.div<{ $variant: ResourcePickerData['resource'] }>(({ $variant }) => ({
  width: pxToRem(36),
  height: pxToRem(36),
  borderRadius: pxToRem(10),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.color.backgroundPrimary,
  border: `1px solid ${theme.color.border10}`,
  flexShrink: 0,
  overflow: 'hidden',
  padding: $variant === 'ai_agent' ? 0 : pxToRem(2),
}));

const CardImage = styled.img({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});
